import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Calculator,
  FileCheck,
  Building2,
  Download,
  ChevronDown,
  Sparkles,
  Gift,
  UserCircle,
  FolderKanban,
  FileStack,
  BarChart3,
} from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { HeroIllustration } from '../components/illustrations/HeroIllustration';
import { FeatureIllustration } from '../components/illustrations/FeatureIllustration';
import { SeoMeta } from '../components/SeoMeta';
import { useAuth } from '../lib/AuthContext';

const FEATURES = [
  {
    icon: Calculator,
    title: 'Berekeningen die kloppen',
    description: 'Op basis van normen en materialen. Je weet waar de cijfers vandaan komen.',
    illustration: 'berekeningen' as const,
  },
  {
    icon: FileCheck,
    title: 'Offerte en begroting',
    description: 'Maak in één klik een offerte of begroting. Met duidelijke onderbouwing.',
    illustration: 'offerte' as const,
  },
  {
    icon: Building2,
    title: 'Transformatie en renovatie',
    description: 'Van uitbouw tot splitsing, van badkamer tot verduurzaming.',
    illustration: 'renovatie' as const,
  },
  {
    icon: Download,
    title: 'Export en rapportage',
    description:
      'Exporteer calculatie naar CSV (Excel), begroting-PDF en meer — met vooraf in te stellen kostenopbouw en regeldetail. Offerte apart voor je klant.',
    illustration: 'export' as const,
  },
];

/** Vier-tab workflow — gelijk aan dossier in de app (Fase 4 productverhaal). */
const WORKFLOW_STEPS = [
  {
    step: 1,
    title: 'Gegevens',
    Icon: FolderKanban,
    text: 'Context, klanttype, documentenbibliotheek; tekening of PDF analyseren (ook opnieuw vanuit de bibliotheek).',
  },
  {
    step: 2,
    title: 'Posten',
    Icon: FileStack,
    text: 'Prijsboek invullen en **Regels opslaan** — daarna ontgrendel je Resultaat en Rapportage.',
  },
  {
    step: 3,
    title: 'Resultaat',
    Icon: BarChart3,
    text: 'Totalen en prijsopbouw controleren — dezelfde library-logica als in je posten.',
  },
  {
    step: 4,
    title: 'Rapportage',
    Icon: FileCheck,
    text: 'Calculatie-export (CSV, Excel, PDF) en offerte uit dezelfde bron.',
  },
] as const;

const FAQ = [
  {
    q: 'Voor wie is CalqTool™?',
    a: 'Voor iedereen die bouwkosten wil berekenen: particulieren, aannemers, projectontwikkelaars, banken en woningbouwcorporaties.',
  },
  {
    q: 'Hoe werkt de tekening-analyse?',
    a: 'Upload of kies documenten uit je dossier-bibliotheek. Het resultaat blijft op het dossier; je importeert posten in delen naar een conceptvoorstel. Klanttype stuurt de diepte van de scan; duidelijke bestandsnamen helpen. Op Posten kies je hoe het voorstel met bestaande regels samengaat — toevoegen, vervangen of samenvoegen — en bevestig je expliciet. Details: footer → Tekening & PDF.',
  },
  {
    q: 'Wat is het verschil tussen calculatie-export en offerte?',
    a: 'Op tab Rapportage (na inloggen) download je calculatiebestanden (CSV, Excel, begroting-PDF) voor interne uitwerking. Je stelt vooraf in hoe uitgebreid die export is: kostenopbouw, regeldetail (soort/bouwdeel/systeem), per-deel, CO₂ en voorbehoud. De offerte is een aparte, klantgerichte preview plus print/PDF — dezelfde cijfers, andere presentatie. Meer uitleg: footer → Export & offerte.',
  },
  {
    q: 'Zijn de uitkomsten bindend?',
    a: 'Nee. De berekeningen zijn indicatief. Voor definitieve offertes en vergunningen heb je altijd een vakman nodig.',
  },
  {
    q: 'Hoe werkt de proefperiode?',
    a: 'Na registratie kun je CalqTool 7 dagen uitproberen met dezelfde functionaliteit als een betaald abonnement, binnen limieten (richtlijn: tot 2 tekeninganalyses, 1 actief dossier en 3 exports totaal — tenzij anders in de app vermeld). De proefperiode stopt zodra die limieten bereikt zijn of de 7 dagen om zijn — wat eerder komt. Daarna kies je Basis, Plus of Pro; Enterprise op aanvraag. Zie de algemene voorwaarden.',
  },
  {
    q: 'Welke abonnementen zijn er?',
    a: 'Basis (€ 99/mnd), Plus (€ 199/mnd) en Pro (€ 399/mnd) excl. btw — of jaarabonnement met ca. 16,7% korting (€ 990 / € 1.990 / € 3.990 excl. btw). Enterprise is maatwerk op aanvraag. Als accountbeheerder (meestal de eerste gebruiker van je organisatie) sluit je een abonnement af via de app (Account → Facturatie) met Stripe (iDEAL, kaart, enz.). Zie ook /prijzen.',
  },
  {
    q: 'Wat is een accountbeheerder en mag ik op meerdere apparaten inloggen?',
    a: 'De accountbeheerder regelt voor de organisatie o.a. de naam en het abonnement (Account → Facturatie). Andere gebruikersaccounts zien dat menu niet. Hetzelfde e-mailadres mag op meerdere apparaten tegelijk ingelogd zijn, tot een maximum per pakket: Basis één apparaat tegelijk, Plus drie, Pro vijf (Enterprise ruimer). Nieuwe login op een extra apparaat kan de oudste actieve sessie beëindigen. Details: na inloggen onder Pakketten en onder Account → Inloggen & apparaten.',
  },
];

function FaqItem({ item, isOpen, onToggle }: { item: typeof FAQ[0]; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="rounded-lg border border-border bg-surface-card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex min-h-[44px] w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-surface"
      >
        <h3 className="font-medium text-text">{item.q}</h3>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {isOpen && (
        <div className="border-t border-border px-4 py-3">
          <p className="text-sm text-text-muted">{item.a}</p>
        </div>
      )}
    </div>
  );
}

export function Landing() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const { user, isLoading, isAccountManager } = useAuth();
  const showAppHero = !isLoading && !!user?.authenticated;

  return (
    <div>
      <SeoMeta
        path="/"
        title="Bouwcalculaties voor transformaties, splitsingen en renovaties"
        description="Bouwkosten berekenen voor renovatie, transformatie en woningsplitsing. CalqTool™ combineert een deterministische NL-prijsbibliotheek met dossiers, documenten en optionele tekeninganalyse. Export begroting en offerte — onderbouwde totalen."
      />

      {/* Hero */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <AnimatedSection>
              {showAppHero ? (
                <>
                  <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-medium text-emerald-800">
                    <UserCircle className="h-4 w-4" aria-hidden />
                    Je bent ingelogd
                  </div>
                  <h1 className="mb-4 text-3xl font-bold text-text sm:text-4xl">
                    Welkom terug bij CalqTool™
                  </h1>
                  <p className="mb-6 text-lg text-text-muted">
                    Ga verder met je dossiers, of start een nieuwe calculatie.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link
                      to="/dashboard"
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:bg-primary-alt"
                    >
                      Naar dashboard
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                    <Link
                      to="/projects/new"
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-6 py-3 font-medium text-text transition-all duration-200 hover:scale-[1.02] hover:bg-surface"
                    >
                      Nieuw dossier
                    </Link>
                    <Link
                      to="/onboarding"
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-6 py-3 font-medium text-text transition-all duration-200 hover:scale-[1.02] hover:bg-surface"
                    >
                      Introductie
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                    <Sparkles className="h-4 w-4" aria-hidden />
                    7 dagen proefperiode — met limieten
                  </div>
                  <h1 className="mb-4 text-3xl font-bold text-text sm:text-4xl">
                    Bouwcalculaties voor transformaties, splitsingen en renovaties
                  </h1>
                  <p className="mb-6 text-lg text-text-muted">
                    CalqTool™ helpt je bouwkosten te berekenen. Geen giswerk – je ziet precies waar het op gebaseerd is.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link
                      to="/login?returnTo=%2Fprojects%2Fnew"
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:bg-primary-alt"
                    >
                      Start proefperiode
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                    <Link
                      to="/demo"
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-6 py-3 font-medium text-text transition-all duration-200 hover:scale-[1.02] hover:bg-surface"
                    >
                      Live product-demo
                    </Link>
                    <Link
                      to="/login?returnTo=%2Fonboarding"
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-6 py-3 font-medium text-text transition-all duration-200 hover:scale-[1.02] hover:bg-surface"
                    >
                      Zo werkt CalqTool
                    </Link>
                    <Link
                      to="/login?returnTo=%2Fdashboard"
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-6 py-3 font-medium text-text transition-all duration-200 hover:scale-[1.02] hover:bg-surface"
                    >
                      Naar dashboard
                    </Link>
                  </div>
                </>
              )}
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="flex justify-center">
                <HeroIllustration
                  className="w-full max-w-md"
                  alt="Illustratie: persoon bekijkt bouwkosten en calculaties op een laptop"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Trust — partner-logo-1 = illustratie groep professionals */}
      <section className="border-t border-border bg-surface-card py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <p className="text-center text-sm font-semibold uppercase tracking-wider text-text-muted">
            Vertrouwd door professionals
          </p>
          <figure className="mx-auto mt-8 flex max-w-2xl flex-col items-center px-2">
            <img
              src="/assets/partner-logo-1.png"
              alt="Illustratie: diverse bouw- en vastgoedprofessionals die CalqTool gebruiken"
              className="h-auto w-full max-h-[min(420px,55vh)] object-contain"
              loading="lazy"
              decoding="async"
            />
            <figcaption className="mt-4 max-w-lg text-center text-sm font-medium leading-snug text-text">
              Professionals in aanneming, ontwikkeling, corporaties en financiering
            </figcaption>
          </figure>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center text-sm font-medium text-text sm:text-base">
            <span className="font-bold text-primary">520+</span>
            <span className="text-text-muted">professionals actief</span>
            <span className="hidden text-border sm:inline" aria-hidden>
              ·
            </span>
            <span className="font-bold text-primary">120+</span>
            <span className="text-text-muted">aannemers &amp; bouwteams</span>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-sm text-text sm:gap-x-4">
            <span className="rounded-full border border-border bg-surface px-3 py-1 font-medium">Aannemers</span>
            <span className="text-text-muted" aria-hidden>
              ·
            </span>
            <span className="rounded-full border border-border bg-surface px-3 py-1 font-medium">Projectontwikkelaars</span>
            <span className="text-text-muted" aria-hidden>
              ·
            </span>
            <span className="rounded-full border border-border bg-surface px-3 py-1 font-medium">Woningcorporaties</span>
            <span className="text-text-muted" aria-hidden>
              ·
            </span>
            <span className="rounded-full border border-border bg-surface px-3 py-1 font-medium">Banken</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-2xl font-bold text-text">
            Waarom CalqTool™?
          </h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <AnimatedSection key={i} delay={0.08 * i} stagger={0}>
                <div className="rounded-xl border border-border bg-surface p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" data-animate>
                  <div className="mb-4 inline-flex rounded-lg bg-primary/10 p-3">
                    <f.icon className="h-6 w-6 text-primary" aria-hidden />
                  </div>
                  <h3 className="mb-2 font-semibold text-text">{f.title}</h3>
                  <p className="text-sm text-text-muted">{f.description}</p>
                  <div className="mt-4 flex justify-center opacity-90">
                    <FeatureIllustration variant={f.illustration} className="h-20 w-auto" />
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Één lijn: dossier = vier tabs (zelfde woorden als in de app) */}
      <section className="border-t border-border py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-2 text-center text-2xl font-bold text-text">Zo loopt je dossier</h2>
          <p className="mx-auto mb-10 max-w-2xl text-center text-sm text-text-muted">
            In de app werk je op één projectpagina met <strong className="text-text">vier vaste tabs</strong> — dezelfde
            volgorde als in de introductie na inloggen.
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WORKFLOW_STEPS.map((s, i) => (
              <AnimatedSection key={s.step} delay={0.06 * i} stagger={0}>
                <div
                  className="flex h-full flex-col rounded-xl border border-border bg-surface-card p-5 shadow-sm"
                  data-animate
                >
                  <div className="mb-3 flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
                      {s.step}
                    </span>
                    <s.Icon className="h-6 w-6 text-primary" aria-hidden />
                  </div>
                  <h3 className="mb-2 font-semibold text-text">{s.title}</h3>
                  <p className="text-sm text-text-muted">
                    {s.text.split('**').map((chunk, j) =>
                      j % 2 === 1 ? (
                        <strong key={j} className="font-semibold text-text">
                          {chunk}
                        </strong>
                      ) : (
                        <span key={j}>{chunk}</span>
                      )
                    )}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-xs text-text-muted">
            Optioneel: tussen Gegevens en je vaste posten een <strong className="text-text">tekeningvoorstel</strong>{' '}
            keuren — met dezelfde tab-navigatie en zijbalk-stappen als elders op het dossier.
          </p>
        </div>
      </section>

      {/* Pakketten (samenvatting → volledige tabel) */}
      <section className="border-t border-border bg-surface py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-8 overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-r from-primary/[0.12] via-surface-card to-primary/[0.08] px-5 py-4 shadow-sm sm:px-8 sm:py-5">
            <p className="text-center text-sm font-semibold text-primary sm:text-base">
              Meest gekozen: <span className="text-text">Plus</span> — voldoende ruimte voor kleine teams, meer analyses
              en exports. Jaarabonnement met ca. 16,7% korting.
            </p>
          </div>
          <h2 className="mb-2 text-center text-2xl font-bold text-text">Pakketten die bij je passen</h2>
          <p className="mx-auto mb-10 max-w-2xl text-center text-sm text-text-muted">
            Basis, Plus en Pro — vaste prijzen en limieten. Enterprise op maat voor grote organisaties. Alle prijzen{' '}
            <strong className="text-text">exclusief BTW</strong>.
          </p>
          <div className="grid gap-6 sm:grid-cols-3">
            <AnimatedSection delay={0.05}>
              <div className="flex h-full flex-col rounded-xl border border-border bg-surface-card p-6 shadow-sm transition hover:shadow-md">
                <h3 className="text-lg font-semibold text-text">Basis</h3>
                <p className="mt-1 text-sm text-text-muted">ZZP en eerste serieuze gebruik</p>
                <p className="mt-4 text-2xl font-bold text-primary">€ 99</p>
                <p className="text-xs text-text-muted">per maand excl. btw · € 990/jaar</p>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-text-muted">
                  <li>1 gebruiker · 2 actieve dossiers</li>
                  <li>5 tekeninganalyses / maand</li>
                  <li>10 exports / maand</li>
                </ul>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="relative flex h-full flex-col overflow-hidden rounded-xl border-2 border-primary bg-surface-card p-6 shadow-lg ring-2 ring-primary/15">
                <span className="absolute right-4 top-4 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Beste waarde
                </span>
                <h3 className="text-lg font-semibold text-text">Plus</h3>
                <p className="mt-1 text-sm text-text-muted">Vaste gebruiker, klein team</p>
                <p className="mt-4 text-2xl font-bold text-primary">€ 199</p>
                <p className="text-xs text-text-muted">per maand excl. btw · € 1.990/jaar</p>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-text-muted">
                  <li>1–3 gebruikers · 5 dossiers</li>
                  <li>20 analyses / maand</li>
                  <li>40 exports / maand</li>
                </ul>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <div className="flex h-full flex-col rounded-xl border border-border bg-surface-card p-6 shadow-sm transition hover:shadow-md">
                <h3 className="text-lg font-semibold text-text">Pro</h3>
                <p className="mt-1 text-sm text-text-muted">Drukke aannemer, werkvoorbereiding</p>
                <p className="mt-4 text-2xl font-bold text-primary">€ 399</p>
                <p className="text-xs text-text-muted">per maand excl. btw · € 3.990/jaar</p>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-text-muted">
                  <li>3–10 gebruikers · 15 dossiers</li>
                  <li>60 analyses / maand</li>
                  <li>120 exports / maand · API</li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
          <div className="mt-10 flex flex-col items-center gap-4">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Link
                to="/prijzen"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:bg-primary-alt"
              >
                Volledige vergelijking en Enterprise
              </Link>
              <Link
                to="/login?returnTo=%2Fprojects%2Fnew"
                className="text-sm font-medium text-primary hover:underline"
              >
                Start proefperiode →
              </Link>
              {showAppHero && isAccountManager ? (
                <Link
                  to="/account/billing"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Abonnement afrekenen (beheerder) →
                </Link>
              ) : null}
              {!showAppHero ? (
                <Link
                  to="/login?returnTo=%2Faccount%2Fbilling"
                  className="text-sm font-medium text-text-muted hover:text-primary hover:underline"
                >
                  Inloggen als beheerder om af te rekenen →
                </Link>
              ) : null}
            </div>
            <p className="max-w-xl text-center text-xs text-text-muted">
              Na de proefperiode: pakket kiezen en betalen via <strong className="text-text">Stripe</strong> onder{' '}
              <strong className="text-text">Account → Facturatie</strong> (alleen tenant-beheerders).
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-surface-card py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="mb-8 text-center text-2xl font-bold text-text">
            Veelgestelde vragen
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <AnimatedSection key={i} delay={0.03 * i}>
                <FaqItem
                  item={item}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-navy py-14 text-center text-white">
        <div className="mx-auto flex max-w-2xl flex-col items-center px-4">
          {showAppHero ? (
            <>
              <p className="mb-2 text-lg font-semibold text-white sm:text-xl">Ga verder in CalqTool</p>
              <p className="mb-6 text-sm text-white/80">Je dossiers en projecten wachten op je.</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:bg-primary-alt"
                >
                  Dashboard
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link
                  to="/projects/new"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/5 px-6 py-3 font-medium text-white transition-all duration-200 hover:border-white/50 hover:bg-white/10"
                >
                  Nieuw dossier
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/5 px-6 py-3 font-medium text-white transition-all duration-200 hover:border-white/50 hover:bg-white/10"
                >
                  Neem contact op
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-sky/40 bg-sky/15 px-4 py-2 text-sm font-semibold text-sky">
                <Gift className="h-4 w-4 shrink-0" aria-hidden />
                7 dagen proefperiode
              </div>
              <p className="mb-2 text-base font-medium text-sky sm:text-lg">
                Volledige flow tot offerte en export — binnen limieten. Geen creditcard nodig.
              </p>
              <p className="mb-6 text-lg font-semibold text-white sm:text-xl">
                Klaar om te beginnen?
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/login?returnTo=%2Fprojects%2Fnew"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:bg-primary-alt"
                >
                  Start proefperiode
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/5 px-6 py-3 font-medium text-white transition-all duration-200 hover:border-white/50 hover:bg-white/10"
                >
                  Neem contact op
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
